import { Config } from 'config/envConfig';
import axios from 'axios';
import {
    faCheckCircle,
    faClose,
    faExclamationTriangle,
    faPencil,
    faPlus,
    faSave,
    faStop,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { message, Modal } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, TextArea } from '@quyva/shared-ui';
import { getUrl } from '../../../pdfDisplay/utils';
import { alert, AlertType } from '@quyva/shared-ui';
import { LoadingView } from '@quyva/shared-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Table, Badge, Tag } from 'antd';
import { toSentenceCase } from '@quyva/common';

const RuleStatus = Object.freeze({
    ACTIVE: 'active',
    INACTIVE: 'inactive',
});

const State = Object.freeze({
    SUCCESS: 'success',
    ERROR: 'error',
    DANGER: 'danger',
    WARNING: 'warning',
});

const cleanAndSplitInput = (value) => {
    return value
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
};

const arrayToCommaSeparated = (arr = []) =>
    arr
        .filter((item) => item && item.toString().trim() !== '')
        .map((item) => item.trim())
        .join(', ');

const OrganisationConfiguration = ({ setConfigureOrganisationMode }) => {
    const [organizationRules, setOrganizationRules] = useState([]);
    const [identificationFields, setIdentificationFields] = useState([]);
    const [organisationID, setOrganisationID] = useState(null);
    const user = JSON.parse(localStorage.getItem('current'));
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [newRuleMode, setNewRuleMode] = useState(false);

    const fetchOrganizationId = async () => {
        try {
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/details`
            );
            const authToken = encodeURIComponent(user.token);

            const response = await axios.get(url, {
                headers: {
                    Authorization: authToken,
                },
            });

            const data = response.data;
            setOrganisationID(data.details.id);
            await fetchOrgRulesAndFIelds(data.details.id);
        } catch (err) {
            // console.error('Error fetching organization details:', err)
            message.error(
                'Error fetching organization details. Please try again later.'
            );
        }
    };

    async function fetchOrgRulesAndFIelds(org_id) {
        const url = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/organisation_config/get_org_details/${org_id}`
        );
        const authToken = encodeURIComponent(user.token);

        try {
            const response = await axios.get(url, {
                headers: {
                    Authorization: authToken,
                    'Content-Type': 'application/json',
                },
            });

            const data = response.data;
            const { rules, validation_fields } = data;

            // Parse the rules if it's a string, otherwise use as is
            const parsedRules =
                typeof rules === 'string' ? JSON.parse(rules) : rules;

            const modifiedRules = parsedRules.map((data, index) => {
                return {
                    key: index + 1,
                    rule: data,
                    status: data.status ?? RuleStatus.ACTIVE,
                };
            });

            const modifiedFields = cleanAndSplitInput(validation_fields);

            setOrganizationRules(modifiedRules);
            setIdentificationFields(modifiedFields);
        } catch (error) {
            // console.log('Error fetching organization details:', error.message)
            message.error(
                error.response?.data?.detail ||
                    error.message ||
                    'Error fetching organization details. Please try again later.'
            );
        }
    }

    useEffect(() => {
        fetchOrganizationId();
    }, []);

    const validateRules = (rulesData) => {
        const newErrors = { ...errors };
        if (rulesData.length === 0)
            newErrors.rules = 'At least one rule is required';
        else if (rulesData.some((r) => r.rule === null || r.rule === ''))
            newErrors.rules = 'Some of rules are empty in the table';
        else delete newErrors.rules;
        setErrors(newErrors);
    };

    const validateFields = (fields) => {
        const newErrors = { ...errors };
        if (fields.length === 0)
            newErrors.fields = 'Identification fields cannot be empty';
        else delete newErrors.fields;
        setErrors(newErrors);
    };

    const validate = (rulesData, fields) => {
        const newErrors = {};
        if (rulesData.length === 0)
            newErrors.rules = 'At least one rule is required';

        if (fields.length === 0)
            newErrors.fields = 'Identification fields cannot be empty';
        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const saveOrganizationRulesAndFields = async () => {
        // Modifying data for backend adaptation later needs to be changed to incorporate status field
        const modifiedRules = organizationRules.map((data) => data.rule);

        if (!validate(modifiedRules, identificationFields)) {
            message.error('Please fill all required fields before saving.');
            return;
        }
        try {
            setIsLoading(true);
            const url = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/organisation_config/add_org_details/`
            );
            const authToken = encodeURIComponent(user.token);

            const formData = new FormData();
            formData.append('org_id', organisationID);
            // Stringify the rules array before sending
            let rules = [];
            modifiedRules.forEach((rule) => {
                if (rule.replaceAll(' ', '') !== '') {
                    rules.push(rule);
                }
            });
            formData.append('rules', JSON.stringify(rules));
            formData.append(
                'fields',
                arrayToCommaSeparated(identificationFields)
            );

            await axios.post(url, formData, {
                headers: {
                    Authorization: authToken,
                },
            });

            alert(AlertType.SUCCESS, `Save Successful`);
            // Close modal
            setConfigureOrganisationMode(false);
        } catch (err) {
            alert(
                AlertType.ERROR,
                `Error Saving Organization Access Control Rules`
            );
        } finally {
            setIsLoading(false);
        }
    };

    return isLoading ? (
        <div className='flex justify-center items-center w-[70vw] h-[79vh]'>
            <LoadingView loadingText={'Loading Access Control Rules...'} />
        </div>
    ) : (
        <div className='flex flex-col justify-between items-center w-[70vw] h-[79vh] p-4'>
            <div className='w-full h-[68vh] overflow-auto'>
                <div className='border rounded-xl p-2 bg-backgrounds-primary100'>
                    <AccessControlRulesTable
                        data={organizationRules}
                        setData={setOrganizationRules}
                        pagination={8}
                        validateData={validateRules}
                        newRuleMode={newRuleMode}
                        setNewRuleMode={setNewRuleMode}
                    />
                    {errors.rules && !newRuleMode && (
                        <div className='text-red-500 text-sm mt-2'>
                            {errors.rules}
                        </div>
                    )}
                </div>

                <div className='border rounded-xl p-2 mt-4 bg-backgrounds-primary100'>
                    <IdentifierFields
                        data={identificationFields}
                        setData={setIdentificationFields}
                        validateData={validateFields}
                    />
                    {errors.fields && (
                        <div className='text-red-500 text-sm mt-2'>
                            {errors.fields}
                        </div>
                    )}
                </div>
            </div>

            <div className='w-full items-center justify-end flex pt-2'>
                <Button
                    onClick={() => {
                        setConfigureOrganisationMode(false);
                    }}
                    text='Cancel'
                    icon={faClose}
                    type='danger'
                    className='w-fit px-4 ml-4'
                    disabled={false}
                />

                <Button
                    onClick={() => {
                        saveOrganizationRulesAndFields();
                    }}
                    text='Save'
                    icon={faSave}
                    type='success'
                    className='w-fit px-4 ml-4'
                    hoverText='Save Access Control Rules And Identification Fields for the Organization'
                    disabled={
                        Object.keys(errors).length > 0 ||
                        organizationRules.length === 0 ||
                        organizationRules.some(
                            (r) => r.rule === null || r.rule === ''
                        ) ||
                        identificationFields.length === 0
                    }
                />
            </div>
        </div>
    );
};

const AccessControlRulesTable = ({
    data,
    setData,
    pagination,
    validateData,
    newRuleMode,
    setNewRuleMode,
}) => {
    const [searchText, setSearchText] = useState('');
    const [editingKey, setEditingKey] = useState(null);
    const [editValue, setEditValue] = useState('');

    const inputRef = useRef(null);

    useEffect(() => {
        if (editingKey && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingKey]);

    useEffect(() => {
        validateData(data);
    }, [data]);

    // Handle edit button click
    const handleEdit = (record) => {
        setEditingKey(record.key);
        setEditValue(record.rule);
    };

    // Save edited value
    const handleSave = (key) => {
        if (editValue !== null) {
            setData((prev) =>
                prev.map((item) =>
                    item.key === key ? { ...item, rule: editValue } : item
                )
            );
        }
        setEditingKey(null);
        setEditValue(null);
        if (newRuleMode) {
            setNewRuleMode(false);
        }
    };

    // Delete a row
    const handleDelete = (key) => {
        setData((prev) => prev.filter((item) => item.key !== key));
    };

    // Add new row
    const handleAdd = () => {
        const newKey = data.length
            ? Math.max(...data.map((d) => d.key)) + 1
            : 1;
        const newData = {
            key: newKey,
            rule: null,
            status: RuleStatus.ACTIVE,
        };
        setNewRuleMode(true);
        setData([newData, ...data]);
        setEditingKey(newKey);
        setEditValue(null);
    };

    // Search filter
    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchText.toLowerCase())
        )
    );

    // Table columns
    const columns = [
        {
            title: 'Rules',
            dataIndex: 'rule',
            key: 'rule',
            width: '70%',
            sorter: (a, b) => a.rule.localeCompare(b.rule),
            render: (index, record) => {
                if (editingKey === record.key) {
                    return (
                        <TextArea
                            ref={inputRef}
                            rows={2}
                            cols={100}
                            value={editValue}
                            placeholder={`Enter New Access Control Rule ${record.key}`}
                            onChange={(e) => {
                                const { selectionStart, selectionEnd } =
                                    e.target;
                                setEditValue(e.target.value);
                                setTimeout(() => {
                                    if (inputRef.current) {
                                        const nativeInput = inputRef.current
                                            .resizableTextArea
                                            ? inputRef.current.resizableTextArea
                                                  .textArea
                                            : inputRef.current;

                                        if (nativeInput) {
                                            nativeInput.selectionStart =
                                                selectionStart;
                                            nativeInput.selectionEnd =
                                                selectionEnd;
                                        }
                                    }
                                }, 0);
                            }}
                            // onPressEnter={() => handleSave(record.key)}
                            onBlur={() => handleSave(record.key)}
                        />
                    );
                }
                return record.rule ? (
                    <div className='w-full'>
                        {record.rule ? record.rule.trim() : record.rule}
                    </div>
                ) : (
                    <div className='w-full text-texts-red400 font-semibold px-1'>
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className='mr-2'
                        />
                        <span>Access Control Rule cannot be empty</span>
                    </div>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '15%',
            render: (status) => (
                <div>
                    <Badge
                        status={
                            status === RuleStatus.ACTIVE
                                ? State.SUCCESS
                                : State.ERROR
                        }
                        text={toSentenceCase(status)}
                    />
                </div>
            ),
            sorter: (a, b) => a.status.localeCompare(b.status),
            filters: Object.values(RuleStatus).map((status) => {
                return {
                    text: toSentenceCase(status),
                    value: status,
                };
            }),
            filterMode: 'tree',
            // filterSearch: true,
            onFilter: (value, record) => record.status.startsWith(value),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '10%',
            render: (_, record) => (
                <div className='flex'>
                    <Button
                        type='warning'
                        icon={faPencil}
                        hoverText={'Edit Access Control Rule'}
                        onClick={() => handleEdit(record)}
                        className='mr-2'
                    />
                    {/* <Button
                        type='success'
                        icon={
                            record.status === RuleStatus.ACTIVE
                                ? faStop
                                : faCheckCircle
                        }
                        hoverText={
                            record.status === RuleStatus.ACTIVE
                                ? 'Disable Access Control Rule'
                                : 'Enable Access Control Rule'
                        }
                        onClick={() =>
                            setData((prev) =>
                                prev.map((item) =>
                                    item.key === record.key
                                        ? {
                                              ...item,
                                              status:
                                                  item.status ===
                                                  RuleStatus.ACTIVE
                                                      ? RuleStatus.INACTIVE
                                                      : RuleStatus.ACTIVE,
                                          }
                                        : item
                                )
                            )
                        }
                        className='mr-2'
                    /> */}

                    <Button
                        type='danger'
                        hoverText={'Delete Access Control Rule'}
                        icon={faTrash}
                        onClick={() => handleDelete(record.key)}
                    />
                </div>
            ),
        },
    ];

    return (
        <div>
            <div className='flex justify-between items-center mb-1'>
                <div className='w-1/3 font-semibold text-texts-secondary300'>
                    Access Control Rules
                </div>
                <div className='w-2/3 flex justify-between h-10'>
                    <Input
                        placeholder='🔍 Search in table'
                        prefix={null}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    <Button
                        type='success'
                        text={'New Rule'}
                        hoverText={'Add new access control rule in table'}
                        icon={faPlus}
                        onClick={handleAdd}
                        className='ml-4'
                    />
                </div>
            </div>
            <Table
                columns={columns}
                dataSource={filteredData}
                pagination={
                    pagination && filteredData.length > pagination
                        ? { pageSize: pagination }
                        : false
                }
                bordered
                size='small'
                scroll={{ x: 'max-content', y: 'max-content' }}
            />
        </div>
    );
};

const IdentifierFields = ({ data, setData, validateData }) => {
    const [inputValue, setInputValue] = useState('');
    const textareaRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (textareaRef.current) textareaRef.current.focus();
    }, []);

    useEffect(() => {
        validateData(data);
    }, [data]);

    const handleAddFields = () => {
        if (!inputValue.trim()) return;

        const newTags = cleanAndSplitInput(inputValue);
        if (newTags.length === 0) {
            alert(AlertType.error, 'No valid identifiers found.');
            setInputValue('');
            return;
        }

        const existing = new Set(data);
        const uniqueTags = newTags.filter((tag) => !existing.has(tag));

        if (uniqueTags.length === 0) {
            alert(AlertType.WARNING, 'Identifier(s) already exist.');
        } else {
            setData((prev) => [...prev, ...uniqueTags]);
        }

        setInputValue('');
    };

    const handleClearAllFields = () => {
        setData([]);
    };

    const handleDelete = (tagToDelete) => {
        setData(data.filter((tag) => tag !== tagToDelete));
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            buttonRef?.current?.click(); // Programmatically click the button
        }
    };

    const sortedTags = [...data].sort(); // simple lexicographic (case-sensitive) sort

    return (
        <div className='w-full'>
            <div className='w-full flex mb-2 items-center justify-between'>
                <label className='w-1/3 font-semibold text-texts-secondary300 font-semibold'>
                    Identifier Fields
                </label>
                <div className='w-2/3 flex items-center justify-between'>
                    <Input
                        ref={textareaRef}
                        placeholder={
                            'Enter the identifier fields separated by comma that will correspond to enterprise user'
                        }
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className='h-10'
                        onKeyDown={handleKeyDown}
                    />

                    <Button
                        ref={buttonRef}
                        type='success'
                        icon={faPlus}
                        text={'Add'}
                        hoverText={'Add new identifier field'}
                        onClick={handleAddFields}
                        className={'ml-2'}
                        disabled={!inputValue}
                    />

                    <Button
                        type='danger'
                        icon={faTrash}
                        text={'Clear All'}
                        hoverText={'Add new identifier field'}
                        onClick={handleClearAllFields}
                        className={'ml-2'}
                        disabled={sortedTags.length === 0}
                    />
                </div>
            </div>
            <div
                className={
                    'w-full min-h-8 border rounded-xl bg-backgrounds-white py-4 px-2 overflow-auto'
                }
            >
                {sortedTags.length === 0 ? (
                    <div className='flex w-full h-8 justify-center items-center bg-backgrounds-warning font-normal'>
                        <div className='flex w-full justify-center items-center text-texts-secondary300'>
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className={'mr-2'}
                            />
                            No identifiers
                        </div>
                    </div>
                ) : (
                    sortedTags.map((tag) => (
                        <Tag
                            key={tag}
                            closable
                            onClose={() => handleDelete(tag)}
                            closeIcon={<FontAwesomeIcon icon={faClose} />}
                            style={{ fontSize: '1rem', margin: '0.3rem' }}
                        >
                            {tag}
                        </Tag>
                    ))
                )}
            </div>
        </div>
    );
};

export default OrganisationConfiguration;